import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAuthMessage, setShowAuthMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();

    // Check if redirected from protected route
    if (location.state?.message) {
      setShowAuthMessage(true);
      // Clear the message after 5 seconds
      setTimeout(() => setShowAuthMessage(false), 5000);
    }
  }, [location]);

  async function checkAuthStatus() {
    try {
      await getCurrentUser();
      setIsAuthenticated(true);
    } catch {
      setIsAuthenticated(false);
    } finally {
      // Slight delay for smoother appearance
      setTimeout(() => setIsLoading(false), 300);
    }
  }

  const allModes = [
    {
      title: 'Tutorial Mode',
      description: 'Step-by-step lessons to master Claude Code fundamentals',
      badge: 'Recommended',
      icon: '📚',
      path: '/tutorial',
      variant: 'primary' as const,
      requiresAuth: true,
      gradient: 'from-primary-500/10 to-primary-600/5',
    },
    {
      title: 'Escape Room',
      description: 'Learn through narrative-driven puzzle adventures',
      badge: 'Interactive',
      icon: '🔐',
      path: '/escape-room',
      variant: 'outline' as const,
      requiresAuth: true,
      gradient: 'from-purple-500/10 to-pink-600/5',
    },
    {
      title: 'Practice Mode',
      description: 'Practice freely with project templates',
      badge: 'Practice',
      icon: '🎨',
      path: '/sandbox',
      variant: 'outline' as const,
      requiresAuth: false,
      gradient: 'from-blue-500/10 to-cyan-600/5',
    },
    {
      title: 'Challenge Mode',
      description: 'Test your skills with timed coding challenges',
      badge: 'Advanced',
      icon: '⚡',
      path: '/challenge',
      variant: 'outline' as const,
      requiresAuth: true,
      gradient: 'from-yellow-500/10 to-orange-600/5',
    },
  ];

  // Filter modes based on authentication
  const modes = isAuthenticated
    ? allModes
    : allModes.filter(mode => !mode.requiresAuth);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-dark-100 to-black flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-primary-500/20 rounded-full animate-spin border-t-primary-400 mx-auto"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-primary-400/20 rounded-full animate-ping mx-auto"></div>
          </div>
          <p className="text-primary-300 animate-pulse">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-dark-100 to-black p-8 overflow-hidden">
      {/* Animated background elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary-600/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Auth Required Message */}
        {showAuthMessage && (
          <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-slide-down">
            <div className="bg-primary-500/20 border border-primary-500/50 text-primary-200 px-6 py-3 rounded-lg shadow-lg backdrop-blur-sm hover:bg-primary-500/30 transition-all duration-300">
              <p className="font-medium">🔒 Please sign in to access this feature</p>
            </div>
          </div>
        )}

        {/* Header - Staggered fade-in */}
        <div className="text-center mb-16 pt-12 space-y-4">
          <h1
            className="text-6xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-400 via-primary-300 to-primary-400
                       drop-shadow-[0_0_30px_rgba(168,232,118,0.3)]
                       animate-fade-in"
            style={{ animationDelay: '0ms' }}
          >
            CodeQuest
          </h1>
          <p
            className="text-2xl text-primary-300 animate-fade-in"
            style={{ animationDelay: '200ms', opacity: 0, animationFillMode: 'forwards' }}
          >
            Master Claude Code
          </p>
          <p
            className="text-lg text-primary-200/70 animate-fade-in max-w-2xl mx-auto"
            style={{ animationDelay: '400ms', opacity: 0, animationFillMode: 'forwards' }}
          >
            Learn AI-assisted development through interactive challenges
          </p>
        </div>

        {/* Call to Action for Non-Authenticated Users - Show First */}
        {!isAuthenticated && (
          <div className="max-w-6xl mx-auto space-y-6 mb-16">
            {/* Unlock Full Access Banner */}
            <div
              className="animate-slide-up"
              style={{
                animationDelay: '600ms',
                opacity: 0,
                animationFillMode: 'forwards'
              }}
            >
              <Card
                variant="elevated"
                className="
                  border border-primary-500/20
                  bg-gradient-to-br from-primary-500/10 via-dark-100 to-primary-600/10
                  hover:border-primary-400/30
                  transition-all duration-500
                  hover:shadow-[0_20px_60px_-15px_rgba(168,232,118,0.4)]
                  backdrop-blur-sm
                  relative overflow-hidden
                  group
                "
              >
                {/* Animated gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary-400/0 via-primary-500/0 to-primary-600/0 group-hover:from-primary-400/5 group-hover:via-primary-500/5 group-hover:to-primary-600/10 transition-all duration-700"></div>

                <CardContent className="text-center py-20 px-8 relative z-10">
                  <div className="text-7xl mb-8 inline-block animate-bounce" style={{ animationDuration: '3s' }}>
                    🚀
                  </div>
                  <h2 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-primary-300 mb-6">
                    Unlock Full Access
                  </h2>
                  <p className="text-primary-200/90 mb-10 text-xl max-w-2xl mx-auto leading-relaxed">
                    Sign up to access tutorials, challenges, escape rooms, and track your progress!
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() => navigate('/signup')}
                      className="px-12 py-4 text-lg transform hover:scale-105 transition-all duration-300 hover:shadow-[0_10px_40px_-10px_rgba(168,232,118,0.6)]"
                    >
                      Sign Up Free
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => navigate('/login')}
                      className="px-12 py-4 text-lg transform hover:scale-105 transition-all duration-300"
                    >
                      Sign In
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Practice Mode Banner */}
            <div
              className="animate-slide-up"
              style={{
                animationDelay: '750ms',
                opacity: 0,
                animationFillMode: 'forwards'
              }}
            >
              <Card
                variant="elevated"
                className="
                  group cursor-pointer
                  border border-primary-500/20
                  transition-all duration-500 ease-out
                  hover:scale-[1.01] hover:-translate-y-1
                  hover:shadow-[0_20px_60px_-15px_rgba(168,232,118,0.3)]
                  hover:border-primary-400/30
                  bg-gradient-to-br from-blue-500/10 to-cyan-600/5
                  backdrop-blur-sm
                  relative overflow-hidden
                "
                onClick={() => navigate('/sandbox')}
              >
                {/* Hover glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary-400/0 to-primary-600/0 group-hover:from-primary-400/5 group-hover:to-primary-600/10 transition-all duration-500"></div>

                <CardContent className="text-center py-20 px-8 relative z-10">
                  <div className="text-7xl mb-8 inline-block transform group-hover:scale-110 transition-transform duration-300">
                    🎨
                  </div>
                  <h2 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-primary-300 mb-6 group-hover:from-primary-300 group-hover:to-primary-400 transition-all duration-300">
                    Practice Mode
                  </h2>
                  <p className="text-primary-200/90 mb-10 text-xl max-w-2xl mx-auto leading-relaxed group-hover:text-primary-200 transition-colors duration-300">
                    Practice freely with project templates - no time limits, no scoring
                  </p>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/sandbox');
                    }}
                    className="px-12 py-4 text-lg transform hover:scale-105 transition-all duration-300"
                  >
                    Start Practicing
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Game Modes - For Authenticated Users */}
        {isAuthenticated && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            {modes.map((mode, index) => (
              <div
                key={mode.path}
                className="animate-slide-up"
                style={{
                  animationDelay: `${600 + index * 150}ms`,
                  opacity: 0,
                  animationFillMode: 'forwards'
                }}
              >
                <Card
                  variant="elevated"
                  className={`
                    group cursor-pointer
                    transition-all duration-500 ease-out
                    hover:scale-[1.02] hover:-translate-y-2
                    hover:shadow-[0_20px_60px_-15px_rgba(168,232,118,0.3)]
                    hover:border-primary-400/60
                    bg-gradient-to-br ${mode.gradient}
                    backdrop-blur-sm
                    relative overflow-hidden
                  `}
                  onClick={() => navigate(mode.path)}
                >
                  {/* Hover glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-400/0 to-primary-600/0 group-hover:from-primary-400/5 group-hover:to-primary-600/10 transition-all duration-500"></div>

                  <CardHeader className="relative z-10">
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-5xl transform group-hover:scale-110 transition-transform duration-300">
                        {mode.icon}
                      </span>
                      <Badge
                        variant="info"
                        size="sm"
                        className="group-hover:bg-primary-500/20 transition-colors duration-300"
                      >
                        {mode.badge}
                      </Badge>
                    </div>
                    <CardTitle className="group-hover:text-primary-300 transition-colors duration-300">
                      {mode.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="relative z-10">
                    <p className="text-primary-200/80 mb-4 group-hover:text-primary-200 transition-colors duration-300">
                      {mode.description}
                    </p>
                    <Button
                      variant={mode.variant}
                      size="sm"
                      className="w-full transform group-hover:scale-105 transition-all duration-300"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(mode.path);
                      }}
                    >
                      {mode.title === 'Tutorial Mode' ? 'Start Learning' : 'Explore'}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        )}

        {/* Features - Staggered animation */}
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center"
          style={{
            animationDelay: `${800 + modes.length * 150}ms`,
          }}
        >
          {[
            { icon: '🎯', title: 'Hands-on Learning', desc: 'Practice with real code examples and interactive exercises', delay: 0 },
            { icon: '🏆', title: 'Track Progress', desc: 'Earn achievements, XP, and unlock new content as you learn', delay: 150 },
            { icon: '🚀', title: 'Level Up Skills', desc: 'From beginner to expert with progressive difficulty', delay: 300 },
          ].map((feature, index) => (
            <div
              key={feature.title}
              className="
                p-8 rounded-lg
                bg-gradient-to-br from-primary-500/5 to-transparent
                border border-primary-500/10
                hover:border-primary-500/30
                hover:bg-primary-500/10
                transition-all duration-500
                transform hover:-translate-y-1
                animate-fade-in
                group
              "
              style={{
                animationDelay: `${1000 + modes.length * 150 + feature.delay}ms`,
                opacity: 0,
                animationFillMode: 'forwards'
              }}
            >
              <div className="text-4xl mb-4 inline-block transform group-hover:scale-110 transition-transform duration-300">
                {feature.icon}
              </div>
              <h3 className="text-primary-400 font-semibold mb-3 text-lg group-hover:text-primary-300 transition-colors duration-300">
                {feature.title}
              </h3>
              <p className="text-primary-200/70 text-sm leading-relaxed group-hover:text-primary-200/90 transition-colors duration-300">
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
